/**
 * Usage Dashboard Page
 *
 * Shows the user's plan, monthly usage allowances (local safety limits, not
 * credits), plan limits and recent usage activity.
 * Route: /usage
 */

import { poolSafetyLimitText } from '../utils/billingStatusView';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  useGetMyUsageQuery,
  getActionLabel,
  type UsageAction,
} from '../store/api/usageApi';
import { PlanLimitsCard } from '../components/usage/PlanLimitsCard';

export function UsageDashboardPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { data: usageData, isLoading, isError, refetch } = useGetMyUsageQuery(undefined, {
    skip: !isAuthenticated,
  });

  if (!isAuthenticated) {
    navigate('/login?returnTo=/usage');
    return null;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage & Limits</h1>
          <p className="mt-1 text-gray-500">
            Track your usage against your plan's allowances and limits
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn-secondary text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Allowances vs limits — two different things, stated once */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="usage-credits-vs-limits">
        <p className="text-sm text-blue-800">
          Monthly allowances cap metered actions such as voice analyses; they are usage limits,
          not credits, and cannot be bought. Plan limits cap how many sessions and analyses you
          can start in a day or month. Both are shown below.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800">Failed to load usage data. Please try again.</p>
          <button onClick={() => refetch()} className="mt-3 btn-primary text-sm">
            Retry
          </button>
        </div>
      ) : usageData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plan Info Card */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Your Plan</h2>
              <span className="badge-primary">{usageData.plan.planLabel}</span>
            </div>
            <div className="card-body space-y-4">
              {/* Capabilities */}
              <div className="grid grid-cols-2 gap-3">
                <CapabilityBadge
                  label="AI Features"
                  enabled={usageData.plan.capabilities.canUseAI}
                />
                <CapabilityBadge
                  label="Voice Practice"
                  enabled={usageData.plan.capabilities.canUseLiveVoice}
                />
                <CapabilityBadge
                  label="Voice Feedback"
                  enabled={usageData.plan.capabilities.canUseTTS}
                />
                <CapabilityBadge
                  label="Practice not metered per use (session limits apply)"
                  enabled={usageData.plan.capabilities.unlimitedPractice}
                />
              </div>

              <button
                onClick={() => navigate('/settings/billing')}
                className="btn-primary w-full"
              >
                Manage Plan in LevelUp
              </button>
            </div>
          </div>

          {/* Monthly allowances card (local safety limits — not credits) */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Monthly Allowances Left</h2>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <AllowanceBox
                  label="Practice"
                  pool="practice"
                  unlimited={usageData.plan.capabilities.unlimitedPractice}
                  remaining={usageData.safetyAllowance?.remaining?.practice}
                  monthly={usageData.safetyAllowance?.monthly?.practice}
                  colorClass="blue"
                />
                <AllowanceBox
                  label="AI voice coaching"
                  pool="coaching"
                  unlimited={false}
                  remaining={usageData.safetyAllowance?.remaining?.coaching}
                  monthly={usageData.safetyAllowance?.monthly?.coaching}
                  colorClass="purple"
                />
                <AllowanceBox
                  label="Full listens"
                  pool="listen"
                  unlimited={usageData.plan.capabilities.unlimitedListening}
                  remaining={usageData.safetyAllowance?.remaining?.listen}
                  monthly={usageData.safetyAllowance?.monthly?.listen}
                  colorClass="green"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">Allowances reset at the start of each month.</p>
            </div>
          </div>

          {/* Plan Limits Card — every number and sentence from the server */}
          <PlanLimitsCard limits={usageData.limits ?? null} />

          {/* Usage Summary Card */}
          <div className="card lg:col-span-2">
            <div className="card-header flex justify-between items-center">
              <h2 className="font-semibold text-gray-900">Usage Summary</h2>
              {usageData.usage.lastUsedAt && (
                <span className="text-sm text-gray-500">
                  Last activity: {formatDate(usageData.usage.lastUsedAt)}
                </span>
              )}
            </div>
            <div className="card-body">
              {/* Total allowance units used */}
              <div className="text-center mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">
                  {usageData.usage.totalUnitsConsumed ?? '—'}
                </p>
                <p className="text-sm text-gray-500 mt-1">Allowance Units Used (all time)</p>
              </div>

              {/* Usage by Action */}
              {Object.keys(usageData.usage.totalsByAction).length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">By Action Type</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(usageData.usage.totalsByAction).map(([action, count]) => (
                      <div
                        key={action}
                        className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                      >
                        <span className="text-sm text-gray-700">
                          {getActionLabel(action as UsageAction)}
                        </span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  No usage recorded yet. Start practicing to see your activity here!
                </p>
              )}
            </div>
          </div>

          {/* Recent Activity Card */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="card-body">
              {usageData.usage.recent.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Action
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Quantity
                        </th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          Units
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                          When
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.usage.recent.map((record, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900">
                            {getActionLabel(record.action)}
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-gray-600">
                            {record.quantity}
                          </td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className={record.unitsConsumed > 0 ? 'text-amber-600' : 'text-gray-400'}>
                              -{record.unitsConsumed}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-500">
                            {formatTimeAgo(record.occurredAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No recent activity. Your usage will appear here as you practice.
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Helper components

function CapabilityBadge({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div
      className={`flex items-center gap-2 p-2 rounded-lg ${
        enabled ? 'bg-green-50' : 'bg-gray-100'
      }`}
    >
      {enabled ? (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span className={`text-sm ${enabled ? 'text-green-800' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
}

function AllowanceBox({
  label,
  pool,
  unlimited,
  remaining,
  monthly,
  colorClass,
}: {
  label: string;
  pool: 'practice' | 'coaching' | 'listen';
  /**
   * The pool is not metered per use on this plan (capabilities.unlimitedPractice
   * / unlimitedListening). Rendered "Not metered per use" with the safety limit
   * that still applies — never "Unlimited" (RC19).
   */
  unlimited: boolean;
  /** undefined = unknown: rendered "Unavailable", never 0 */
  remaining: number | undefined;
  monthly: number | undefined;
  colorClass: 'blue' | 'purple' | 'green';
}) {
  const known = typeof remaining === 'number' && typeof monthly === 'number';
  const percentage = known && monthly! > 0 ? Math.round((remaining! / monthly!) * 100) : 100;
  const isLow = known && !unlimited && percentage < 25;

  const bgColorMap = {
    blue: 'bg-blue-50',
    purple: 'bg-purple-50',
    green: 'bg-green-50',
  };

  const textColorMap = {
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    green: 'text-green-600',
  };

  return (
    <div className={`text-center p-4 rounded-lg ${bgColorMap[colorClass]}`}>
      <p className={`text-2xl font-bold ${isLow ? 'text-red-500' : textColorMap[colorClass]}`}>
        {unlimited ? 'Not metered per use' : known ? remaining : 'Unavailable'}
      </p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
      {unlimited && poolSafetyLimitText(pool) && (
        <p className="text-xs text-gray-400 mt-0.5" data-testid={`allowance-safety-${pool}`}>
          {poolSafetyLimitText(pool)}; session limits apply
        </p>
      )}
      {!unlimited && known && monthly! > 0 && (
        <p className="text-xs text-gray-400 mt-0.5">of {monthly}</p>
      )}
    </div>
  );
}

// Date formatting helpers

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
